import { ResponsiveLine } from "@nivo/line";
import ExpandableChartCard from "../../../../shared/charts/ExpandableChartCard";
import { NIVO_DARK_THEME } from "./HeadcountChart";

/**
 * codex.md — Revenue / Cost / Profit as Nivo Area charts.
 *
 * Three series per chart in the fixed order [Actual, Expected, Forecast].
 * Axis-family colours:
 *   Revenue  Actual #22c55e emerald
 *   Cost     Actual #ef4444 red
 *   Profit   Actual #3b82f6 blue
 * Expected is always amber (#f59e0b); Forecast is always slate (#94a3b8)
 * rendered with a dashed stroke so planned vs reality reads at a glance.
 *
 * Forecast dashing is done via Nivo's `defs` + `fill` pattern override
 * at the SVG line level — we key the dashed style off series id so it
 * works regardless of category order. Area fill is 18% opacity so
 * overlapping series remain readable.
 */

const FMT_SEK = (n) => `${Math.round(n).toLocaleString()} kr`;
const FMT_PCT = (n) => `${Math.round(n)}%`;

function toNivoRows(data) {
  const months = Array.isArray(data) ? data : [];
  const ids = ["Actual", "Expected", "Forecast"];
  return ids.map((id) => ({
    id,
    data: months.map((r) => ({ x: r.month, y: Number(r[id]) || 0 })),
  }));
}

function AreaChart({ data, colors, valueFormatter, expanded }) {
  const nivoRows = toNivoRows(data);
  const colorMap = { Actual: colors[0], Expected: colors[1], Forecast: colors[2] };

  return (
    <div style={{ height: expanded ? "100%" : 256, width: "100%" }}>
      <ResponsiveLine
        data={nivoRows}
        theme={NIVO_DARK_THEME}
        colors={({ id }) => colorMap[id] || "#64748b"}
        margin={{ top: 18, right: 24, bottom: 40, left: 72 }}
        xScale={{ type: "point" }}
        yScale={{ type: "linear", min: "auto", max: "auto", stacked: false }}
        curve="monotoneX"
        axisBottom={{ tickSize: 0, tickPadding: 8 }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          format: (v) => valueFormatter(v),
        }}
        enableArea
        areaOpacity={0.18}
        enablePoints
        pointSize={6}
        pointBorderWidth={2}
        pointBorderColor={{ from: "serieColor" }}
        pointColor="#070e20"
        useMesh
        animate={false}
        enableSlices="x"
        sliceTooltip={({ slice }) => (
          <div
            style={{
              background: "#070e20",
              color: "#dce8f8",
              border: "1px solid #14213d",
              borderRadius: 6,
              fontSize: 12,
              padding: "8px 10px",
            }}
          >
            <div style={{ color: "#8fa4c8", marginBottom: 4 }}>
              {slice.points[0]?.data?.xFormatted}
            </div>
            {slice.points.map((p) => (
              <div
                key={p.id}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: p.serieColor,
                  }}
                />
                <span style={{ flex: 1 }}>{p.serieId}</span>
                <strong>{valueFormatter(p.data.y)}</strong>
              </div>
            ))}
          </div>
        )}
        // Dashed Forecast line: Nivo exposes a `layers` prop; replacing
        // the default "lines" layer with a custom renderer lets us set
        // strokeDasharray per series id. Area fill still comes from the
        // "areas" layer rendered above.
        layers={[
          "grid",
          "markers",
          "axes",
          "areas",
          "crosshair",
          ({ series, lineGenerator, xScale, yScale }) => (
            <g>
              {series.map((s) => (
                <path
                  key={s.id}
                  d={lineGenerator(
                    s.data.map((d) => ({
                      x: xScale(d.data.x),
                      y: yScale(d.data.y),
                    })),
                  )}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray={s.id === "Forecast" ? "6 4" : undefined}
                />
              ))}
            </g>
          ),
          "slices",
          "points",
          "mesh",
          "legends",
        ]}
      />
    </div>
  );
}

function AreaLegend({ colors }) {
  const items = [
    { id: "Actual", color: colors[0], dashed: false },
    { id: "Expected", color: colors[1], dashed: false },
    { id: "Forecast", color: colors[2], dashed: true },
  ];
  return (
    <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 11, color: "#8fa4c8", flexWrap: "wrap" }}>
      {items.map((it) => (
        <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 18,
              height: 2,
              background: it.dashed
                ? `repeating-linear-gradient(to right, ${it.color} 0 4px, transparent 4px 8px)`
                : it.color,
            }}
          />
          {it.id}
        </div>
      ))}
    </div>
  );
}

// User-requested unified palette for Revenue / Cost / Profit:
// Actual = green, Expected = orange, Forecast = purple (dashed).
const RCP_COLORS = ["#22c55e", "#f59e0b", "#a855f7"];
const RCP_SUBTITLE = "Actual (green) · Expected (orange) · Forecast (purple dashed)";

export function RevenueChart({ data, canSee, year, right }) {
  if (!canSee) return null;
  return (
    <ExpandableChartCard
      title={`Revenue — ${year}`}
      subtitle={RCP_SUBTITLE}
      right={right}
      renderChart={({ expanded }) => (
        <>
          <AreaChart data={data} colors={RCP_COLORS} valueFormatter={FMT_SEK} expanded={expanded} />
          <AreaLegend colors={RCP_COLORS} />
        </>
      )}
    />
  );
}

export function CostChart({ data, canSee, year }) {
  if (!canSee) return null;
  return (
    <ExpandableChartCard
      title={`Cost — ${year}`}
      subtitle={RCP_SUBTITLE}
      renderChart={({ expanded }) => (
        <>
          <AreaChart data={data} colors={RCP_COLORS} valueFormatter={FMT_SEK} expanded={expanded} />
          <AreaLegend colors={RCP_COLORS} />
        </>
      )}
    />
  );
}

export function ProfitChart({ data, canSee, year }) {
  if (!canSee) return null;
  return (
    <ExpandableChartCard
      title={`Profit — ${year}`}
      subtitle={`${RCP_SUBTITLE} · derived`}
      renderChart={({ expanded }) => (
        <>
          <AreaChart data={data} colors={RCP_COLORS} valueFormatter={FMT_SEK} expanded={expanded} />
          <AreaLegend colors={RCP_COLORS} />
        </>
      )}
    />
  );
}

export { FMT_SEK, FMT_PCT };
